<script lang="ts">
    import dayjs from "dayjs";
    import {enhance} from "$app/forms";
    import CardLayout from "$items/CardLayout.svelte";
    import type {Employee} from "$lib/types/employee";
    import InfoBox from "$items/InfoBox.svelte";
    import type {Writable} from "svelte/store";
    import {getContext, onMount} from "svelte";
    import {BuildingSolid, PenOutline} from "flowbite-svelte-icons";
    import {Button, Hr} from "flowbite-svelte";
    import VerticalGrid from "$lib/components/VerticalGrid.svelte";
    import {asSelectItems, genderOptions, maritalStatus, statusOptions} from "$lib/options";
    import ApplyTrigger from "$items/employee/ApplyTrigger.svelte";
    import {page} from "$app/stores";
    import EmployeeLanguages from "$items/employee/EmployeeLanguages.svelte";
    import CardLayoutSubmitButton from "$items/employee/form/CardLayoutSubmitButton.svelte";
    import {getUnits} from "$lib/calls/unit";
    import IncompleteDataWarning from "$lib/components/IncompleteDataWarning.svelte";
    import {get} from "$lib/api";
    import {allUnits} from "$lib/stores/unit";
    import {referenceCountries, referenceEducation} from "$lib/stores/references";

    export let display = true;
    export let readOnly = false;
    let loading = false;
    let employee: Writable<Employee> = getContext('employee');

    let super_edit: Writable<boolean> = getContext('super_edit');

    let units: any[] = [];

    let affiliationEntered = $employee?.contracts.length > 0 ? $employee.contracts?.sort((a, b) => b.id - a.id)[0].history[0]?.startDate?.value : null;



    let stati: string[] = [];

    $: complete = $employee.firstName && $employee.lastName && $employee.gender && $employee.cv.placeOfBirth
        && $employee.maidenName && $employee.dateOfBirth && $employee.cv.familyStatus && $employee.cv.degree
        && $employee.status && $employee.cv.countryOfBirth

    onMount(async () => {
        if($allUnits.length === 0){
            $allUnits = await getUnits();
        }
        units = $allUnits.map((item:any) => ({...item, name: item.name, value: item.id}))
        stati = (await get('/hr/references/stati')).map((item:any) => ({name:item, value:item}))
        if($referenceCountries.length === 0){
            $referenceCountries = (await get('/hr/reference/Staaten')).map((item:any) => ({...item, name:item.value})).sort((a, b) => a.name.localeCompare(b.name))
        }
        if($referenceEducation.length === 0){
            $referenceEducation = (await get('/hr/reference/Ausbildung')).map((item:any) => ({...item, name:item.value}))
        }
    })

</script>

<CardLayout bind:display title="Stammdaten" accordion >
    <svelte:fragment slot="card-header">
        <IncompleteDataWarning {complete}/>
        {#if !readOnly}
            <Button color="yellow" class="!p-2" pill outline on:click={() => $super_edit = !$super_edit}>
                <PenOutline class="w-4 h-4"/>
            </Button>
        {/if}
    </svelte:fragment>
    <form method="post"
          class:opacity-20={loading}
          use:enhance={() => {loading=true;return ({ update }) => {loading=false; update({ reset: false })}}}
          action="?/update_base_data">

        <VerticalGrid>
                <InfoBox title="Vorname" value={$employee.firstName} name="firstName"/>
                <InfoBox title="Geschlecht" value={$employee.gender} items={asSelectItems(genderOptions)} type="select" name="gender"/>
                <InfoBox title="Staatsangehörigkeit" value={$employee.cv?.nationality} name="cv_nationality"/>
                <InfoBox title="Geburtsnachname" value={$employee.maidenName} name="maidenName"/>
                <InfoBox title="Familienstand"
                         value={$employee.cv?.familyStatus || '-'} name="cv_familyStatus"
                         items={Object.entries(maritalStatus).map(([key, value]) => ({value:key, name:value}))}
                         type="select">
                </InfoBox>
                <InfoBox title="Aktueller Status" value={$employee.status} items={stati} type="select" name="status"/>
            <svelte:fragment slot="right">
                <InfoBox title="Nachname" value={$employee.lastName} name="lastName"/>
                <InfoBox title="Geburtsort" name="cv_placeOfBirth" value={$employee.cv?.placeOfBirth}/>
                <InfoBox title="Geburtsland"
                         items={$referenceCountries} type="select"
                         name="cv_countryOfBirth" value={$employee.cv?.countryOfBirth}/>
                <div class="flex gap-2">
                    <div class="flex-1">
                        <InfoBox title="Geburtsdatum"
                                 name="dateOfBirth"
                                 type="date"
                                 value={$employee.dateOfBirth?.value ? dayjs($employee.dateOfBirth.value, 'YYYY-MM-DD').format('YYYY-MM-DD') : ''}/>
                    </div>

                    <div class="pt-7">{dayjs().diff(dayjs($employee.dateOfBirth?.value), 'years', false)} Jahre</div>
                </div>

                <InfoBox title="Ausbildung" items={$referenceEducation} type="select" value={$employee.cv?.degree} name="cv_degree"/>

                {#if $employee.status === 'Hauptbeschäftigt'}
                    <InfoBox title="Beschäftigt bei" value={$employee.cv.mainEmployer}  name="cv_mainEmployer"/>
                {/if}
            </svelte:fragment>
        </VerticalGrid>
        <Hr/>
        <VerticalGrid>

            <InfoBox title="Quelle" value={$employee.reference} readonly/>
            <InfoBox name="unitId" title="Unit" type="select" items={units} value={$employee.unitId}/>
            <svelte:fragment slot="right">
                <InfoBox title="Firmeneintritt" readonly
                         value={affiliationEntered ? dayjs(affiliationEntered, 'YYYY-MM-DD').format('DD.MM.YYYY') : ''}/>
                <InfoBox title="Verifiziert am" readonly value={dayjs.unix($employee.createdAt?.stamp).format('DD.MM.YYYY')}/>
                <div class="pt-2"></div>
            </svelte:fragment>

        </VerticalGrid>
        <CardLayoutSubmitButton/>
        <ApplyTrigger {loading}/>
    </form>
    <div class="pt-5">
        <EmployeeLanguages bind:employee={$employee}/>
    </div>

</CardLayout>